import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import { portalAPI } from '../../api';
import { getClientAreaSignInPath } from '../../lib/authFailureRedirect';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { CreditCard, Loader2, ExternalLink, Home } from 'lucide-react';

/**
 * Public Pay page: pay invoice via Stripe using token from link.
 * Query: token (required), payment (success | cancelled).
 * Shows success/cancel toasts, respects payEnabled, redirects to Stripe Checkout.
 */
export default function Pay() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const payment = searchParams.get('payment');
  const stripeSessionId = searchParams.get('session_id')?.trim() || '';
  const portalSlugFromQuery =
    searchParams.get('portalSlug')?.trim() || searchParams.get('slug')?.trim() || '';
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const toastFired = useRef({ success: false, cancelled: false });
  const stripeSyncOnce = useRef(false);
  const [stripeSyncNote, setStripeSyncNote] = useState('');
  const { success: toastSuccess, error: toastError, ToastContainer } = useToast();

  // Fire toasts once when landing with payment=success or payment=cancelled
  useEffect(() => {
    if (payment === 'success' && !toastFired.current.success) {
      toastFired.current.success = true;
      toastSuccess('Payment successful', 'Your payment has been processed.');
    }
    if (payment === 'cancelled' && !toastFired.current.cancelled) {
      toastFired.current.cancelled = true;
      toastError('Payment cancelled', 'You can try again when ready.');
    }
  }, [payment, toastSuccess, toastError]);

  // One-shot: reconcile Checkout session when Stripe appends session_id (fallback if webhooks lag)
  useEffect(() => {
    if (payment !== 'success' || !token || !stripeSessionId || stripeSyncOnce.current) return;
    stripeSyncOnce.current = true;
    let cancelled = false;
    portalAPI
      .syncStripeCheckoutSession(token, stripeSessionId)
      .then((data) => {
        if (cancelled) return;
        if (data?.paymentRecorded === false && data?.pending) {
          setStripeSyncNote(
            'Sync pending: your payment is processing. If the invoice still shows unpaid, your provider should check their Stripe webhook configuration.'
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStripeSyncNote(
            'Sync pending: we could not confirm the payment from the browser. Your provider is notified automatically when Stripe webhooks are working.'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [payment, token, stripeSessionId]);

  // Fetch invoice when token present and not in success/cancel result view
  useEffect(() => {
    if (!token || payment === 'success' || payment === 'cancelled') {
      if (!token) setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    portalAPI
      .getInvoiceByToken(token)
      .then((data) => {
        if (!cancelled) {
          setInvoice(data);
          setError('');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setInvoice(null);
          setError(err.response?.data?.error || err.message || 'Failed to load invoice.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, payment]);

  const portalSlugForLinks = (inv) =>
    portalSlugFromQuery || inv?.tenant?.portalSlug?.trim() || '';

  const portalEntryTo = (inv) => {
    const ps = portalSlugForLinks(inv);
    return `/client/portal-entry?token=${encodeURIComponent(token)}${
      ps ? `&portalSlug=${encodeURIComponent(ps)}` : ''
    }`;
  };

  const viewInvoiceTo = (inv) => {
    const ps = portalSlugForLinks(inv);
    return `/portal/invoice/${token}${ps ? `?portalSlug=${encodeURIComponent(ps)}` : ''}`;
  };

  const paySelfTo = `/client/pay?token=${encodeURIComponent(token)}${
    portalSlugFromQuery ? `&portalSlug=${encodeURIComponent(portalSlugFromQuery)}` : ''
  }`;

  const handlePayWithStripe = async () => {
    if (!token || checkoutLoading) return;
    setCheckoutLoading(true);
    try {
      const { checkoutUrl } = await portalAPI.createCheckout(token);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      setError('Could not start checkout.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Checkout failed. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    typeof amount === 'number' ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount) : '—';

  // No token
  if (!token) {
    const slugHint =
      searchParams.get('portalSlug')?.trim() ||
      searchParams.get('slug')?.trim() ||
      '';
    const signInPath = getClientAreaSignInPath(slugHint || undefined);
    const signInLabel =
      signInPath === '/login' ? 'Go to login' : 'Sign in to client portal';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <ToastContainer />
        <Card className="max-w-md w-full bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">Invalid link</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              This payment link is invalid or missing. Please use the link from your invoice email or contact support.
            </p>
            <div className="flex flex-wrap gap-2">
              <Link to="/">
                <Button variant="outline" className="inline-flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Home
                </Button>
              </Link>
              <Link to={signInPath}>
                <Button variant="default">{signInLabel}</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment cancelled
  if (payment === 'cancelled') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <ToastContainer />
        <Card className="max-w-md w-full bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">Payment cancelled</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">You cancelled the payment. You can try again when ready.</p>
            <Link to={paySelfTo}>
              <Button variant="default">Try again</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment success
  if (payment === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <ToastContainer />
        <Card className="max-w-md w-full bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100 text-green-600 dark:text-green-400">Payment successful</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">Your payment has been processed successfully.</p>
            {stripeSyncNote ? (
              <p className="text-sm text-amber-700 dark:text-amber-300/90" role="status">
                {stripeSyncNote}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Link to={viewInvoiceTo(invoice)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Button variant="outline">
                  <ExternalLink className="h-4 w-4" />
                  View invoice
                </Button>
              </Link>
              <Link to={portalEntryTo(invoice)}>
                <Button variant="default">Go to Client Portal</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <ToastContainer />
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 text-blue-600 dark:text-blue-400 animate-spin" aria-hidden />
          <p className="text-gray-700 dark:text-gray-300">Loading invoice…</p>
        </div>
      </div>
    );
  }

  // Fetch error
  if (error && !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <ToastContainer />
        <Card className="max-w-md w-full bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">Error</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">{error}</p>
            <Link to="/">
              <Button variant="outline">Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invoice loaded: pay disabled
  if (invoice && invoice.payEnabled === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <ToastContainer />
        <Card className="max-w-md w-full bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">Pay invoice</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Online payment is currently disabled for this account. Please contact {invoice.tenant?.name || 'your provider'} to pay by another method.
            </p>
            <div className="flex flex-wrap gap-2">
              <Link to={viewInvoiceTo(invoice)} target="_blank" rel="noopener noreferrer">
                <Button variant="outline">View invoice</Button>
              </Link>
              <Link to={portalEntryTo(invoice)}>
                <Button variant="outline">Go to Client Portal</Button>
              </Link>
              <Link to="/">
                <Button variant="default">Home</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invoice loaded: show pay option (Stripe; extensible for future processors)
  const balanceDue = invoice?.balanceDue ?? 0;
  const canPay = balanceDue > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-8">
      <ToastContainer />
      <Card className="max-w-lg w-full bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-gray-100">Pay invoice</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 p-4 space-y-1">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {invoice?.tenant?.name && <span>{invoice.tenant.name}</span>}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Invoice {invoice?.invoiceNum ?? invoice?.reference ?? '—'} · Balance due {formatCurrency(balanceDue)}
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          )}

          {/* Payment methods: Stripe first; structure allows adding more later */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment method</p>
            {canPay ? (
              <Button
                onClick={handlePayWithStripe}
                disabled={checkoutLoading}
                className="w-full inline-flex items-center justify-center gap-2"
              >
                {checkoutLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <CreditCard className="h-4 w-4" />
                )}
                Pay with Stripe
              </Button>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">This invoice has no balance due.</p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <Link to={viewInvoiceTo(invoice)} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">View invoice</Button>
            </Link>
            <Link to={portalEntryTo(invoice)}>
              <Button variant="ghost" size="sm">Go to Client Portal</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
